/**
 * Phase-1 (demo) compliance UI components: the state-machine spine, risk gauge,
 * tab strip, UBO cascade, and the tone mappings that translate domain states
 * into the design-system palette. Presentational only.
 */
import { useState, type ReactNode } from 'react';
import type {
  EstadoDoc,
  EstadoExpediente,
  NivelRiesgo,
  Persona,
  UboNode,
  ValEstado,
} from '../lib/phase1';
import { CLIENT_FLOW } from '../lib/phase1';
import { Button, Card, Dialog, Label, Pill, cn, type Tone } from './ui';
import { PersonaIcon } from './icons';
import { User } from 'lucide-react';
import { RISK_HEX, CHART_HEX } from '../lib/tokens';
import { useCountUp, usePrefersReducedMotion } from '../lib/motion';

export const estadoTone = (e: EstadoExpediente): Tone =>
  ({
    Nuevo: 'slate',
    'En Revisión': 'blue',
    'Docs Pendientes': 'amber',
    Escalado: 'red',
    Aprobado: 'green',
    Activo: 'green',
    Rechazado: 'red',
    'Actualización Requerida': 'amber',
  } as const)[e];

export const riesgoTone = (r: NivelRiesgo): Tone =>
  ({ bajo: 'green', medio: 'amber', alto: 'red', sin: 'slate' } as const)[r];

export const docTone = (d: EstadoDoc): Tone =>
  ({
    Vigente: 'green',
    Aceptado: 'green',
    'Por Vencer': 'amber',
    Solicitado: 'amber',
    Vencido: 'red',
    'Bajo Revisión': 'blue',
    Recibido: 'blue',
  } as const)[d];

export const valTone = (v: ValEstado): Tone =>
  ({ ok: 'green', hit: 'red', review: 'amber', pending: 'slate' } as const)[v];


/** Client state-machine spine; highlights done / current; off-path states append. */
export function StateSpine({ estado }: { estado: EstadoExpediente }) {
  const onPath = CLIENT_FLOW.includes(estado);
  const idx = CLIENT_FLOW.indexOf(estado);
  const offPath = ['Docs Pendientes', 'Escalado', 'Rechazado'].includes(estado);

  return (
    <div className="flex flex-wrap items-center gap-1 rounded-xl bg-canvas p-3.5">
      {CLIENT_FLOW.map((s, i) => {
        const done = onPath && i < idx;
        const cur = onPath && i === idx;
        return (
          <div key={s} className="flex items-center gap-1">
            <div className="flex min-w-[70px] flex-col items-center gap-1.5">
              <span
                className={cn(
                  'h-3.5 w-3.5 rounded-full border-2 transition-all',
                  done && 'border-primary bg-primary',
                  cur && 'border-primary bg-panel ring-4 ring-primary/15',
                  !done && !cur && 'border-line-strong bg-panel'
                )}
              />
              <span
                className={cn(
                  'text-center text-[10.5px] font-semibold',
                  cur ? 'text-ink' : done ? 'text-primary-dark' : 'text-ink-faint'
                )}
              >
                {s}
              </span>
            </div>
            {i < CLIENT_FLOW.length - 1 && (
              <span className={cn('-mt-4 h-0.5 w-4', done ? 'bg-primary' : 'bg-line-strong')} />
            )}
          </div>
        );
      })}
      {offPath && (
        <>
          <span className="-mt-4 h-0.5 w-4 bg-line-strong" />
          <div className="flex min-w-[80px] flex-col items-center gap-1.5">
            <span
              className={cn(
                'h-3.5 w-3.5 rounded-full border-2 bg-panel',
                estado === 'Escalado' || estado === 'Rechazado'
                  ? 'border-tone-red ring-4 ring-tone-red-bg'
                  : 'border-tone-amber ring-4 ring-tone-amber-bg'
              )}
            />
            <span className="text-center text-[10.5px] font-bold text-ink">{estado}</span>
          </div>
        </>
      )}
    </div>
  );
}

/** Circular risk gauge (conic-gradient) with score, level pill and cadence.
 * `sweep` (opt-in) counts the ring + number up 0→score once, e.g. right after a
 * validation run lands; it self-disables under reduced motion (useCountUp). */
export function RiskGauge({
  score,
  riesgo,
  cadence,
  sweep = false,
}: {
  score: number;
  riesgo: NivelRiesgo;
  cadence: string;
  sweep?: boolean | undefined;
}) {
  const color = RISK_HEX[riesgo];
  const reduced = usePrefersReducedMotion();
  const swept = useCountUp(score, { enabled: sweep && !reduced });
  const shown = sweep && !reduced ? swept : score;
  const deg = Math.max(shown, 4) * 3.6;
  return (
    <Card className="text-center">
      <div
        className="mx-auto grid h-40 w-40 place-items-center rounded-full"
        style={{ background: `conic-gradient(${color} ${deg}deg, ${CHART_HEX.gaugeTrack} 0)` }}
      >
        <div className="grid h-[120px] w-[120px] place-items-center rounded-full bg-panel">
          <div>
            <div className="font-serif text-5xl font-semibold leading-none" style={{ color }}>
              {Math.round(shown)}
            </div>
            <div className="mt-1 font-mono text-[10px] text-ink-faint">/ 100</div>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <Pill tone={riesgoTone(riesgo)}>
          Riesgo {{ bajo: 'Bajo', medio: 'Medio', alto: 'Alto', sin: 'Sin evaluar' }[riesgo]}
        </Pill>
      </div>
      <div className="mt-3 text-xs text-ink-soft">
        Próxima actualización
        <br />
        <b className="font-mono">{cadence}</b>
      </div>
    </Card>
  );
}

export function TabStrip({
  tabs,
  active,
  onChange,
}: {
  tabs: { key: string; label: string; count?: number }[];
  active: string;
  onChange: (k: string) => void;
}) {
  return (
    <div className="mb-5 flex gap-1 overflow-x-auto border-b border-line [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={cn(
            '-mb-px flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-bold transition-colors',
            active === t.key
              ? 'border-primary text-primary-dark'
              : 'border-transparent text-ink-faint hover:text-ink-soft'
          )}
        >
          {t.label}
          {t.count !== undefined && (
            <span className="rounded-full bg-canvas px-1.5 font-mono text-[9.5px] text-ink-faint">
              {t.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export function UboTree({ name, persona, ubo }: { name: string; persona: Persona; ubo: UboNode[] }) {
  return (
    <div>
      <span className="inline-flex items-center gap-2.5 rounded-xl border-2 border-ink bg-panel px-4 py-3 font-bold">
        <PersonaIcon persona={persona} className="h-5 w-5" />
        {name}
      </span>
      <div className="ml-5 mt-4 grid gap-2.5 border-l-2 border-dashed border-line-strong pl-6">
        {ubo.length === 0 && (
          <p className="text-sm text-ink-faint">Sin beneficiarios registrados todavía.</p>
        )}
        {ubo.map((u, i) => (
          <div
            key={i}
            className="flex flex-wrap items-center gap-3 rounded-xl border border-line bg-panel px-4 py-3"
          >
            <span className="grid h-8 w-8 place-items-center rounded-full bg-tone-slate-bg">
              <User className="h-4 w-4 text-tone-slate" strokeWidth={1.75} />
            </span>
            <div className="flex-1">
              <div className="text-sm font-bold">{u.name}</div>
              <div className="font-mono text-[10px] text-ink-faint">{u.role}</div>
            </div>
            <span className="rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 font-mono text-[10px] text-primary-dark">
              ↳ {u.detail}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function FieldGrid({ fields }: { fields: { k: string; v: string }[] }) {
  return (
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-line bg-line md:grid-cols-3">
      {fields.map((f) => (
        <div key={f.k} className="bg-panel px-4 py-3.5">
          <div className="font-mono text-[9.5px] uppercase tracking-wide text-ink-faint">{f.k}</div>
          <div className="mt-1 text-sm font-semibold">{f.v}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Mandatory reason-code modal used on every consequential transition
 * (approve / reject / escalate / request docs / dismiss alert). The regulator
 * can ask why any decision was made, so the reason is required (§ traceability).
 */
export function ReasonModal({
  open,
  title,
  confirmLabel,
  reasons,
  tone = 'default',
  extra,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  confirmLabel: string;
  reasons: string[];
  tone?: 'default' | 'destructive' | undefined;
  /** Optional extra controls rendered above the actions (e.g. a channel picker). */
  extra?: ReactNode | undefined;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const [code, setCode] = useState(reasons[0] ?? '');
  const [note, setNote] = useState('');
  return (
    <Dialog open={open} title={title} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <Label htmlFor="reason-code">Motivo (requerido)</Label>
          <select
            id="reason-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="h-10 w-full rounded-lg border border-line-strong bg-canvas px-3 text-sm"
          >
            {reasons.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="reason-note">Nota (opcional)</Label>
          <textarea
            id="reason-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-line-strong bg-canvas px-3 py-2 text-sm"
            placeholder="Detalle para la bitácora…"
          />
        </div>
        {extra}
        <p className="font-mono text-[10.5px] text-ink-faint">
          🔒 La decisión y su motivo quedan en la bitácora inmutable.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant={tone === 'destructive' ? 'destructive' : 'default'}
            onClick={() => onConfirm(note ? `${code} — ${note}` : code)}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

export function InfoNote({ tone = 'amber', children }: { tone?: 'amber' | 'blue'; children: ReactNode }) {
  return (
    <div
      className={cn(
        'flex gap-3 rounded-xl border p-4 text-sm',
        tone === 'amber'
          ? 'border-tone-amber/40 bg-tone-amber-bg text-tone-amber'
          : 'border-tone-blue/40 bg-tone-blue-bg text-tone-blue'
      )}
    >
      {children}
    </div>
  );
}
